import axios from 'axios';
import {
  AdminLoginResponse,
  ChecklistPayload,
  Order,
  OrderReplacement,
  PagedResponse,
  PaymentQrResponse,
  TechnicianBankAccount,
  TechnicianWallet,
  ValidationResult,
  WalletTransaction,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://cij3b8xauj.execute-api.ap-south-1.amazonaws.com/Prod/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const uploadApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 1 minute — compressed photos are small enough for this
});

const attachAuthToken = (config: any) => {
  const token = localStorage.getItem('technicianToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

const handleAuthError = (error: any) => {
  if (error.response?.status === 401) {
    localStorage.removeItem('technicianToken');
    localStorage.removeItem('technicianUser');
    localStorage.removeItem('technicianTokenExpiresAt');
    window.location.href = '/login';
  }
  return Promise.reject(error);
};

api.interceptors.request.use(attachAuthToken);
uploadApi.interceptors.request.use(attachAuthToken);

api.interceptors.response.use(
  (response) => response,
  handleAuthError
);

uploadApi.interceptors.response.use(
  (response) => response,
  handleAuthError
);

/**
 * Compress an image file using Canvas before upload.
 * Reduces typical 5-10MB phone camera shots to ~300-500KB.
 * Falls back to the original file if compression fails.
 */
async function compressImage(file: File, maxWidthPx = 1280, quality = 0.82): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxWidthPx / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => resolve(blob ?? file),
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

export const apiService = {
  async sendOtp(mobile: string): Promise<ValidationResult<void>> {
    const response = await api.post('/v1/admin-auth/send-otp', { mobile });
    return response.data;
  },

  async verifyOtp(mobile: string, otp: string): Promise<AdminLoginResponse> {
    const response = await api.post('/v1/admin-auth/verify-otp', { mobile, otp });
    return response.data;
  },

  async searchMyOrders(params: {
    page: number;
    pageSize: number;
    searchTerm?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ValidationResult<PagedResponse<Order>>> {
    const response = await api.get('/v1/admin-orders', {
      params: {
        page: 1,
        pageSize: 200,
        fromDate: params.startDate,
        toDate: params.endDate,
      },
    });

    const allOrders = Array.isArray(response.data)
      ? response.data
      : response.data?.entity || response.data?.items || [];

    const searchTerm = (params.searchTerm || '').trim().toLowerCase();
    const filteredOrders = allOrders.filter((order: Order) => {
      const matchesSearch = !searchTerm || [
        order.orderNumber,
        order.customerName,
        order.customerMobile,
        order.makeName,
        order.modelName,
      ].some((value) => (value || '').toLowerCase().includes(searchTerm));

      const matchesStatus = !params.status || order.orderStatus === params.status;
      return matchesSearch && matchesStatus;
    });

    const start = (params.page - 1) * params.pageSize;
    const items = filteredOrders.slice(start, start + params.pageSize);

    return {
      isSuccess: true,
      entity: {
        page: params.page,
        pageSize: params.pageSize,
        totalCount: filteredOrders.length,
        totalPages: Math.max(1, Math.ceil(filteredOrders.length / params.pageSize)),
        items,
      },
    };
  },

  async getOrder(id: number): Promise<ValidationResult<Order>> {
    const response = await api.get(`/v1/admin-orders/${id}`);
    return response.data;
  },

  async uploadBeforePhoto(orderId: number, photo: File, technicianId?: number, onProgress?: (pct: number) => void) {
    const compressed = await compressImage(photo);
    const formData = new FormData();
    formData.append('photo', compressed, 'before.jpg');

    const response = await uploadApi.post(`/v1/order/${orderId}/before-photo`, formData, {
      params: { technicianId },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      },
    });
    return response.data;
  },

  async uploadAfterPhoto(orderId: number, photo: File, technicianId?: number, onProgress?: (pct: number) => void) {
    const compressed = await compressImage(photo);
    const formData = new FormData();
    formData.append('photo', compressed, 'after.jpg');

    const response = await uploadApi.post(`/v1/order/${orderId}/after-photo`, formData, {
      params: { technicianId },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      },
    });
    return response.data;
  },

  async saveChecklist(orderId: number, checklist: ChecklistPayload) {
    const response = await api.post(`/v1/order/${orderId}/checklist`, checklist);
    return response.data;
  },

  async getPaymentQr(orderId: number): Promise<PaymentQrResponse> {
    const response = await api.get(`/v1/payment/qr/${orderId}`);
    return response.data;
  },

  async getPaymentStatus(orderId: number): Promise<string> {
    const response = await api.get(`/v1/payment/status/${orderId}`);
    if (typeof response.data === 'string') {
      return response.data;
    }
    return response.data?.status || response.data?.paymentStatus || response.data?.orderStatus || '';
  },

  async getMyReplacements(technicianId: number, status?: string): Promise<ValidationResult<OrderReplacement[]>> {
    const params: Record<string, any> = { technicianId };
    if (status) params.status = status;
    const response = await api.get('/v1/OrderReplacement', { params });
    return response.data;
  },

  async updateReplacementStatus(replacement: OrderReplacement, newStatus: string): Promise<ValidationResult<void>> {
    const response = await api.put(`/v1/OrderReplacement/${replacement.id}`, {
      ...replacement,
      replacementStatus: newStatus,
    });
    return response.data;
  },

  async updateOrderStatus(orderId: number, status: string, userId: number, paymentMethod?: string) {
    const response = await api.put(`/v1/Order/${orderId}/status`, {
      status,
      notes: paymentMethod ? `Payment via ${paymentMethod}` : '',
      userId,
      paymentMethod,
    });
    return response.data;
  },

  async getLiveSessionByOrder(orderId: number) {
    const response = await api.get(`/v1/LiveRepair/order/${orderId}`);
    return response.data;
  },

  async startLiveSession(payload: {
    orderId: number; orderNumber?: string; technicianId?: number; technicianName?: string;
    makeName?: string; modelName?: string; serviceSummary?: string;
  }) {
    const response = await api.post('/v1/LiveRepair', payload);
    return response.data;
  },

  async endLiveSession(sessionId: number) {
    const response = await api.put(`/v1/LiveRepair/${sessionId}/end`, {});
    return response.data;
  },

  // ── Wallet ──────────────────────────────────────────────────────────────

  async getWallet(technicianId: number): Promise<TechnicianWallet> {
    const response = await api.get(`/v1/technician/${technicianId}/wallet`);
    return response.data;
  },

  async getWalletTransactions(technicianId: number, page = 1, pageSize = 20): Promise<WalletTransaction[]> {
    const response = await api.get(`/v1/technician/${technicianId}/wallet/transactions`, {
      params: { page, pageSize },
    });
    return response.data;
  },

  async getWalletBankAccount(technicianId: number): Promise<TechnicianBankAccount | null> {
    try {
      const response = await api.get(`/v1/technician/${technicianId}/wallet/bank-account`);
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  async requestWithdrawalOtp(technicianId: number): Promise<{ message: string }> {
    const response = await api.post(`/v1/technician/${technicianId}/wallet/withdraw/request-otp`);
    return response.data;
  },

  async confirmWithdrawal(technicianId: number, requestId: number, otp: string): Promise<{ message: string }> {
    const response = await api.post(`/v1/technician/${technicianId}/wallet/withdraw/confirm`, {
      requestId,
      otp,
    });
    return response.data;
  },

  async saveUpiId(technicianId: number, holderName: string, upiId: string): Promise<{ message: string }> {
    const response = await api.post(`/v1/technician/${technicianId}/wallet/upi`, { holderName, upiId });
    return response.data;
  },
};
